import React, { useEffect, useState } from "react";
import moment from "moment";
import { toast } from "react-toastify";
import { useNavigate } from "react-router-dom";

import PanelTemplate from "../../../templates/Panel";
import PageHeader from "../../../components/PageHeader";
import GestaoSettingsTabs from "../../../components/GestaoSettingsTabs";
import Box from "../../../components/Box";
import Button from "../../../components/Button";
import Input from "../../../components/Input";
import InputFile from "../../../components/InputFile";
import InputSelect from "../../../components/InputSelect";
import Toggle from "../../../components/Toggle";
import Icon from "../../../components/Icon";
import { ModalCreateCompany } from "../../../components/ModalCreateCompany";
import ModalEditCompany from "../../../components/ModalEditCompany";

import api from "../../../services/api";
import { renderError } from "../../../helpers/errors";
import { hasPermission } from "../../../services/auth";
import {
    FISCAL_UNITS,
    TAX_REGIMES,
    SPECIAL_TAX_REGIMES,
} from "../helpers";

// Campos que esta tela edita. O payload do save é montado a partir daqui —
// antes a lista era escrita à mão no save e dois campos ficaram de fora
// (o número da próxima nota ficou de fora e não salvava).
const NUMERIC_FIELDS = [
    'nfe_series',
    'nfe_next_number',
];

// nfe_enabled/nfse_enabled não entram aqui: são o ESPELHO do que o provedor
// ativou. nfse_opt_in é outra coisa — é o usuário dizendo que emite
// serviço, e é isso que esta tela edita.
const BOOLEAN_FIELDS = ['send_email_to_customer', 'nfse_opt_in'];

// CFOP, CST/CSOSN e alíquotas saíram daqui: a tributação vive na natureza de
// operação, e esta tela só escolhe qual delas a empresa usa por padrão.
const TEXT_FIELDS = [
    'tax_regime',
    'special_tax_regime',
    'default_fiscal_operation_id',
    'default_fiscal_unit',
];

/**
 * Configuração da emissão fiscal (BDM-887).
 *
 * Checklist com os passos na ordem em que precisam acontecer, cada um pedindo
 * só o que é dele. O cadastro da empresa no emissor NÃO é um passo: é
 * pré-requisito técnico e acontece sozinho no backend, porque não significa
 * nada para quem usa. E o ambiente (teste x real) não é um campo escondido:
 * é um modo, e fica numa barra sempre visível no topo.
 */
export const FiscalSettings = () => {
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [uploading, setUploading] = useState(false);

    // Um tenant pode ter várias empresas de faturamento; cada uma tem CNPJ,
    // certificado e série próprios, então cada uma tem a sua configuração.
    const [issuers, setIssuers] = useState([]);
    const [selectedCompanyId, setSelectedCompanyId] = useState(null);
    const [setting, setSetting] = useState(null);
    const [steps, setSteps] = useState([]);
    const [nfse, setNfse] = useState(null);
    const [openStep, setOpenStep] = useState(null);

    const [form, setForm] = useState({});
    // Naturezas do tenant, para escolher a padrão desta empresa.
    const [operations, setOperations] = useState([]);
    const [certificatePassword, setCertificatePassword] = useState('');
    const [certificateFile, setCertificateFile] = useState(null);
    const [certificateName, setCertificateName] = useState('');
    const [showCreateCompany, setShowCreateCompany] = useState(false);
    // Campos que a tela apontou como pendentes — o modal abre destacando eles.
    const [editingCompanyFields, setEditingCompanyFields] = useState(null);

    const navigate = useNavigate();
    const canManage = hasPermission('manage_fiscal');

    useEffect(() => {
        getSettings();
        getOperations();
    }, []);

    const getOperations = () => {
        api.get(`/fiscal-operation`).then(res => {
            setOperations(res.data.resources ?? []);
        }).catch(renderError);
    };

    const getSettings = (keepCompanyId = null) => {
        setLoading(true);

        api.get(`/fiscal-settings`).then(res => {
            const resources = res.data.resources ?? [];
            setIssuers(resources);

            const current = resources.find(item => item.company.id === (keepCompanyId ?? selectedCompanyId))
                ?? resources[0];

            if (current) {
                selectIssuer(current);
            }
        }).catch(renderError).then(() => setLoading(false));
    };

    const selectIssuer = issuer => {
        setSelectedCompanyId(issuer.company.id);
        setSetting(issuer.setting);
        setSteps(issuer.steps ?? []);
        setNfse(issuer.nfse ?? null);
        setForm(issuer.setting ?? {});

        // Abre automaticamente o primeiro passo pendente da empresa.
        const pending = (issuer.steps ?? []).find(step => !step.done);
        setOpenStep(pending?.key ?? null);
    };

    const updateForm = changes => setForm(current => ({ ...current, ...changes }));

    const editCompany = (fields = []) => setEditingCompanyFields(fields);

    const save = () => {
        setSaving(true);

        const payload = { company_id: selectedCompanyId };

        TEXT_FIELDS.forEach(field => {
            payload[field] = form[field] === '' || form[field] === undefined ? null : form[field];
        });
        NUMERIC_FIELDS.forEach(field => {
            const value = parseInt(form[field], 10);
            payload[field] = Number.isNaN(value) ? null : value;
        });
        BOOLEAN_FIELDS.forEach(field => {
            payload[field] = !!form[field];
        });

        api.put(`/fiscal-settings`, payload).then(res => {
            if (res.data.warning) {
                toast.warn(res.data.warning);
            } else {
                toast.success('Configurações salvas');
            }
            setSetting(res.data.resource);
            setSteps(res.data.steps ?? []);
            if (res.data.nfse) setNfse(res.data.nfse);
            getSettings(selectedCompanyId);
        }).catch(renderError).then(() => setSaving(false));
    };

    const uploadCertificate = () => {
        const file = certificateFile;

        if (!file) {
            toast.error('Escolha o arquivo do certificado (.pfx)');
            return;
        }

        setUploading(true);

        const formData = new FormData();
        formData.append('certificate', file);
        formData.append('password', certificatePassword);
        if (selectedCompanyId) formData.append('company_id', selectedCompanyId);

        api.post(`/fiscal-settings/certificate`, formData, {
            headers: { 'Content-Type': 'multipart/form-data' }
        }).then(res => {
            toast.success('Certificado enviado');
            setSetting(res.data.resource);
            setSteps(res.data.steps ?? []);
            if (res.data.nfse) setNfse(res.data.nfse);
            setCertificatePassword('');
            setCertificateFile(null);
            setCertificateName('');
            setOpenStep('taxes');
            getSettings(selectedCompanyId);
        }).catch(renderError).then(() => setUploading(false));
    };

    const isProduction = (setting?.environment ?? 'sandbox') === 'production';

    // Ambiente é um MODO, não um passo da configuração: define se a nota vale
    // legalmente. Por isso vive numa barra fixa no topo, sempre visível.
    const switchEnvironment = () => {
        const next = isProduction ? 'sandbox' : 'production';

        const confirmed = window.confirm(
            next === 'production'
                ? 'Passar para emissão real? As notas emitidas a partir de agora terão valor legal e irão para a SEFAZ.'
                : 'Voltar para o modo de teste? As notas emitidas deixam de ter valor legal.'
        );

        if (!confirmed) return;

        setSaving(true);
        api.put(`/fiscal-settings`, { company_id: selectedCompanyId, environment: next })
            .then(() => {
                toast.success(next === 'production' ? 'Emissão real ativada' : 'Voltou para o modo de teste');
                getSettings(selectedCompanyId);
            })
            .catch(renderError)
            .then(() => setSaving(false));
    };

    /**
     * A busca das notas destinadas (BDM-1038) vive no provedor, não aqui: o
     * toggle liga lá e só então grava. Certificado vencido derruba a
     * sincronização, e mais de 60 dias parado apaga o acervo na SEFAZ.
     */
    const switchInboundSync = value => {
        setSaving(true);

        api.post(`/fiscal-settings/inbound-sync`, { company_id: selectedCompanyId, enabled: value })
            .then(() => {
                toast.success(value
                    ? 'Busca ativada. As notas aparecem em até 1h20.'
                    : 'Busca desativada.');
                getSettings(selectedCompanyId);
            })
            .catch(renderError)
            .then(() => setSaving(false));
    };

    const certificateAlert = () => {
        if (!setting) return null;

        if (setting.certificate_status === 'expired') {
            return {
                type: 'error',
                text: 'Seu certificado digital venceu. Enquanto ele não for substituído, nenhuma nota pode ser emitida.',
            };
        }

        if (setting.certificate_status === 'expiring') {
            return {
                type: 'warning',
                text: `Seu certificado vence em ${moment(setting.certificate_expires_at).format('DD/MM/YYYY')}. Providencie a renovação para não parar de emitir.`,
            };
        }

        return null;
    };

    const alert = certificateAlert();

    const renderStepBody = step => {
        switch (step.key) {
            case 'company':
                return (
                    <>
                        <p>
                            É este cadastro que aparece impresso na nota. A SEFAZ confere os dados
                            com o que está registrado no CNPJ, então precisam bater exatamente:
                            razão social, inscrição estadual e endereço completo.
                        </p>
                        {(step.problems ?? []).length > 0 &&
                            <ul className={`issue-problems`}>
                                {step.problems.map((problem, index) => (
                                    <li key={index}>
                                        {problem.message}
                                        {(problem.where === 'company' || problem.link) &&
                                            <button onClick={() => (
                                                problem.where === 'company'
                                                    ? editCompany([problem.field])
                                                    : navigate(`${problem.link}?return_to=/fiscal/settings`)
                                            )}>
                                                Corrigir agora
                                            </button>
                                        }
                                    </li>
                                ))}
                            </ul>
                        }
                        <Button
                            type={`secondary`}
                            size={`small`}
                            svg={`edit`}
                            text={`Editar dados da empresa`}
                            action={() => editCompany((step.problems ?? []).map(problem => problem.field))}
                        />
                    </>
                );

            case 'taxes':
                return (
                    <>
                        <p>
                            O regime é o da <strong>empresa</strong>; o CFOP e a situação tributária
                            são da <strong>operação</strong> — cada uma vive na sua natureza de
                            operação.
                        </p>
                        <div className="row">
                            {/* O regime especial de ISS saiu daqui: é da PREFEITURA e
                                só existe por causa da NFS-e — mora no bloco dela. */}
                            <div className="col-12">
                                <InputSelect
                                    menuInBody
                                    label={`Regime tributário da empresa`}
                                    options={TAX_REGIMES}
                                    value={form.tax_regime ?? 'simplesNacional'}
                                    change={value => updateForm({ tax_regime: value })}
                                />
                            </div>
                        </div>
                        <div className="row">
                            <div className="col-6">
                                <InputSelect
                                    menuInBody
                                    label={`Natureza de operação padrão`}
                                    options={operations.map(operation => ({
                                        label: operation.name,
                                        value: operation.id,
                                    }))}
                                    value={form.default_fiscal_operation_id ?? ''}
                                    change={value => updateForm({ default_fiscal_operation_id: value })}
                                />
                                <p className={`fiscal-hint`}>
                                    Usada quando a emissão não escolhe outra
                                </p>
                            </div>
                            <div className="col-6">
                                <InputSelect
                                    menuInBody
                                    label={`Unidade padrão dos produtos`}
                                    options={FISCAL_UNITS}
                                    value={form.default_fiscal_unit ?? 'UN'}
                                    change={value => updateForm({ default_fiscal_unit: value })}
                                />
                            </div>
                        </div>
                        {/* Dizer só "pendente" deixava o usuário adivinhando. São as
                            mesmas pendências que barram a emissão — e fora do Simples
                            elas incluem IBS/CBS e o CST de PIS/COFINS do regime. */}
                        {(step.problems ?? []).length > 0 &&
                            <ul className={`issue-problems`}>
                                {step.problems.map((problem, index) => (
                                    <li key={index}>
                                        {problem.message}
                                        {(problem.where === 'company' || problem.link) &&
                                            <button onClick={() => (
                                                problem.where === 'company'
                                                    ? editCompany([problem.field])
                                                    : navigate(`${problem.link}?return_to=/fiscal/settings`)
                                            )}>
                                                Corrigir agora
                                            </button>
                                        }
                                    </li>
                                ))}
                            </ul>
                        }

                        <p>
                            Na dúvida sobre qual código usar, <strong>pergunte ao seu contador</strong>.
                        </p>
                        <Button
                            type={`secondary`}
                            size={`small`}
                            svg={`external-link`}
                            text={`Configurar naturezas de operação`}
                            action={() => navigate('/fiscal/operations')}
                        />
                    </>
                );

            case 'certificate':
                return (
                    <>
                        <p>
                            O certificado digital <strong>A1</strong> é um arquivo <code>.pfx</code> com
                            senha, emitido pela sua contabilidade ou por uma certificadora. Ele funciona
                            como a assinatura da empresa: sem ele a SEFAZ não aceita nenhuma nota.
                        </p>
                        {setting?.certificate_uploaded_at &&
                            <p className={`fiscal-hint`}>
                                Certificado atual: <strong>{setting.certificate_subject ?? 'enviado'}</strong>
                                {setting.certificate_expires_at &&
                                    <> · vence em {moment(setting.certificate_expires_at).format('DD/MM/YYYY')}</>
                                }
                            </p>
                        }
                        {/* Arquivo e senha lado a lado e estreitos: são um par —
                            um sem o outro não envia nada. */}
                        <div className="row">
                            <div className="col-3">
                                <InputFile
                                    label={`Certificado (.pfx)`}
                                    accept={`.pfx,.p12`}
                                    name={certificateName}
                                    changeName={setCertificateName}
                                    changeFile={setCertificateFile}
                                />
                            </div>
                            <div className="col-4">
                                <Input
                                    label={`Senha do certificado`}
                                    type={`password`}
                                    value={certificatePassword}
                                    change={setCertificatePassword}
                                />
                            </div>
                        </div>
                        <p className={`fiscal-hint`}>
                            Guarde o arquivo original em local seguro: não conseguimos devolvê-lo
                            depois, e a senha não fica salva de forma recuperável.
                        </p>
                        {canManage &&
                            <Button
                                type={`primary`}
                                size={`small`}
                                text={`Enviar certificado`}
                                action={uploadCertificate}
                                loading={uploading}
                            />
                        }
                    </>
                );

            case 'documents':
                return (
                    <>
                        <span className={`fiscal-block-title`}>Nota de produto (NF-e)</span>
                        <div className="row">
                            <div className="col-6">
                                <Input
                                    label={`Série da NF-e`}
                                    value={form.nfe_series ?? 1}
                                    change={value => updateForm({ nfe_series: value })}
                                />
                            </div>
                            <div className="col-6">
                                <Input
                                    label={`Número da próxima NF-e`}
                                    value={form.nfe_next_number ?? ''}
                                    change={value => updateForm({ nfe_next_number: value })}
                                />
                            </div>
                        </div>
                        <p className={`fiscal-hint`}>
                            Série e numeração valem <strong>só para a NF-e</strong> — a numeração da
                            nota de serviço é da prefeitura. Se você já emitia notas de produto em
                            outro sistema, informe o próximo número da sequência: se a última foi a
                            <strong> 500</strong>, preencha <strong>501</strong>.
                        </p>

                        {/* A NFS-e saiu daqui para uma box própria: ela é opcional e
                            tem estado (quer emitir / está ativa), diferente da NF-e.
                            Aqui embaixo ficou só o que o sistema FAZ com as notas —
                            antes esses dois switches vinham intercalados com os tipos
                            de documento. */}
                        <span className={`fiscal-block-title`}>Envio e recebimento</span>
                        <div className={`fiscal-switch-row`}>
                            <div>
                                <strong>Enviar a nota por e-mail ao cliente</strong>
                                <span>
                                    O próprio emissor de notas envia o PDF e o XML assim que a nota é
                                    autorizada, para o e-mail cadastrado no cliente do pedido. O envio
                                    não sai do e-mail da sua empresa.
                                </span>
                            </div>
                            <Toggle
                                checked={!!form.send_email_to_customer}
                                onChange={value => updateForm({ send_email_to_customer: value })}
                                label={`Enviar por e-mail`}
                            />
                        </div>

                        <div className={`fiscal-switch-row`}>
                            <div>
                                <strong>Buscar as notas emitidas contra o seu CNPJ</strong>
                                <span>
                                    As notas que os fornecedores emitem contra este CNPJ passam a chegar
                                    sozinhas em Fiscal › Notas de entrada, na aba "Recebidas da
                                    SEFAZ". A primeira busca leva até 1h20, e só valem as notas
                                    emitidas a partir da ativação.
                                    {setting?.dfe_synced_at &&
                                        ` Última leitura: ${moment(setting.dfe_synced_at).format('DD/MM/YYYY [às] HH:mm')}.`
                                    }
                                </span>
                            </div>
                            <Toggle
                                checked={!!setting?.dfe_enabled}
                                onChange={switchInboundSync}
                                label={`Buscar notas`}
                            />
                        </div>
                    </>
                );

            default:
                return null;
        }
    };

    return (
        <>
            <ModalCreateCompany
                show={showCreateCompany}
                close={() => setShowCreateCompany(false)}
                // Nasce como faturamento: é o tipo que aparece como emitente aqui.
                type={['billing']}
                companyStored={company => {
                    setShowCreateCompany(false);
                    setSelectedCompanyId(company.id);
                    getSettings(company.id);
                    // A empresa nasce sem IE e sem endereço: o modal emenda o
                    // preenchimento aqui mesmo, sem a ida e volta ao cadastro.
                    editCompany([]);
                }}
            />
            <ModalEditCompany
                show={editingCompanyFields !== null}
                close={() => setEditingCompanyFields(null)}
                companyId={selectedCompanyId}
                highlight={editingCompanyFields ?? []}
                intro={`É este cadastro que sai impresso na nota — a SEFAZ confere com o que está registrado no CNPJ.`}
                onSaved={() => {
                    setEditingCompanyFields(null);
                    getSettings(selectedCompanyId);
                }}
            />
        <PanelTemplate id="fiscal-settings">
            <GestaoSettingsTabs />
            <div className="fiscal-settings">
                <PageHeader
                    left={<h2 className={`fiscal-title`}>Emissão de notas fiscais</h2>}
                    right={canManage &&
                        <Button type={`primary`} size={`small`} svg={`save-white`} text={`Salvar`} action={save} loading={saving} />
                    }
                />

                {alert &&
                    <div className={`fiscal-alert ${alert.type}`}>
                        <Icon name={`alert`} />
                        <span>{alert.text}</span>
                    </div>
                }

                {!loading && issuers.length > 0 &&
                    <div className={`fiscal-issuers`}>
                        <span className={`issuers-label`}>Empresa emitente</span>
                        <div className={`issuers-cards`}>
                            {issuers.map(issuer => {
                                const ready = issuer.setting?.is_configured;
                                const pending = (issuer.steps ?? []).filter(step => !step.done).length;

                                return (
                                    <button
                                        key={issuer.company.id}
                                        className={`issuer-card ${selectedCompanyId === issuer.company.id ? 'active' : ''}`}
                                        onClick={() => selectIssuer(issuer)}
                                    >
                                        <strong>{issuer.company.trade_name ?? issuer.company.company_name}</strong>
                                        <small>{issuer.company.cnpj}</small>
                                        <span className={`issuer-badge ${ready ? 'ready' : 'pending'}`}>
                                            {ready
                                                ? 'Emitindo'
                                                : `${pending} ${pending === 1 ? 'passo pendente' : 'passos pendentes'}`
                                            }
                                        </span>
                                    </button>
                                );
                            })}

                            {canManage &&
                                <button
                                    className={`issuer-card new`}
                                    onClick={() => setShowCreateCompany(true)}
                                >
                                    <Icon name={`plus-circle`} />
                                    <strong>Cadastrar empresa</strong>
                                    <small>Outra empresa que emite nota</small>
                                </button>
                            }
                        </div>
                        <p className={`fiscal-hint`}>
                            Cada empresa tem certificado, série e regime próprios. A nota sai pela
                            empresa que fatura o pedido.
                        </p>
                    </div>
                }

                {!loading && setting &&
                    <div className={`fiscal-environment ${isProduction ? 'production' : 'sandbox'}`}>
                        <div className={`environment-text`}>
                            <strong>{isProduction ? 'Emissão real' : 'Modo de teste'}</strong>
                            <span>
                                {isProduction
                                    ? 'As notas emitidas têm valor legal e vão para a SEFAZ.'
                                    : 'As notas emitidas aqui não têm valor legal. Use para conferir tudo antes de valer.'
                                }
                            </span>
                        </div>
                        {canManage &&
                            <Button
                                type={`secondary`}
                                size={`small`}
                                text={isProduction ? 'Voltar para teste' : 'Passar para emissão real'}
                                action={switchEnvironment}
                                loading={saving}
                            />
                        }
                    </div>
                }

                {!loading && setting?.is_configured &&
                    <div className={`fiscal-alert success`}>
                        <Icon name={`check`} />
                        <span>Tudo pronto: você já pode emitir notas pela tela do pedido.</span>
                    </div>
                }

                <Box className={`no-padding`}>
                    <div className={`fiscal-steps`}>
                        {loading &&
                            <div className={`fiscal-step-empty`}>Carregando...</div>
                        }
                        {!loading && issuers.length === 0 &&
                            <div className={`fiscal-step-empty`}>
                                <p>
                                    Você ainda não tem uma empresa de faturamento cadastrada — é ela
                                    que emite as notas em nome do seu negócio.
                                </p>
                                {canManage &&
                                    <Button
                                        type={`primary`}
                                        size={`small`}
                                        svg={`plus-white`}
                                        text={`Cadastrar empresa emitente`}
                                        action={() => setShowCreateCompany(true)}
                                    />
                                }
                            </div>
                        }
                        {!loading && steps.map((step, index) => (
                            <div key={step.key} className={`fiscal-step ${step.done ? 'done' : ''} ${openStep === step.key ? 'open' : ''}`}>
                                <button className={`fiscal-step-head`} onClick={() => setOpenStep(openStep === step.key ? null : step.key)}>
                                    <span className={`step-marker`}>
                                        {step.done ? <Icon name={`check`} /> : index + 1}
                                    </span>
                                    <span className={`step-text`}>
                                        <strong>{step.title}</strong>
                                        <small>{step.description}</small>
                                    </span>
                                    <Icon name={`arrow-dropdown`} className={`step-arrow`} />
                                </button>
                                {openStep === step.key &&
                                    <div className={`fiscal-step-body`}>
                                        {renderStepBody(step)}
                                    </div>
                                }
                            </div>
                        ))}
                    </div>
                </Box>

                {/* Numeração, códigos e envio não são PASSOS — são ajustes que
                    se revisita. Ficam fora do checklist, que agora só tem o
                    que é pré-requisito para começar a emitir. */}
                {!loading && setting &&
                    <Box>
                        <span className={`fiscal-box-title`}>Dados das suas notas</span>
                        {renderStepBody({ key: 'documents' })}
                    </Box>
                }

                {/* Box própria porque a NFS-e é a única coisa desta tela que o
                    usuário LIGA: ela é opcional, depende da prefeitura e tem
                    configuração só dela. Desligada, ocupa uma linha. */}
                {!loading && setting &&
                    <Box>
                        <div className={`fiscal-switch-row`}>
                            <div>
                                <strong>Emitir nota de serviço (NFS-e)</strong>
                                <span>
                                    A nota de serviço é da prefeitura da sua cidade. O serviço
                                    prestado, o código e a alíquota de ISS são informados em cada
                                    nota, na tela de emissão.
                                </span>
                            </div>
                            <Toggle
                                checked={!!form.nfse_opt_in}
                                disabled={!canManage || nfse?.city_supported === false}
                                onChange={value => updateForm({ nfse_opt_in: value })}
                                label={`Emitir nota de serviço`}
                            />
                        </div>

                        {nfse?.city_supported === false &&
                            <p className={`fiscal-hint warning`}>
                                A prefeitura de {nfse.city}/{nfse.state} ainda não é atendida para
                                NFS-e. Quando passar a ser, o botão libera sozinho.
                            </p>
                        }

                        {nfse?.city_supported === null &&
                            <p className={`fiscal-hint`}>
                                Complete o cadastro da empresa para verificarmos se a prefeitura
                                dela é atendida.
                            </p>
                        }

                        {form.nfse_opt_in &&
                            <div className={`fiscal-nfse-body`}>
                                <span className={`issuer-badge ${nfse?.active ? 'ready' : 'pending'}`}>
                                    {nfse?.active ? 'Ativa no emissor' : 'Ainda não ativa'}
                                </span>

                                {(nfse?.problems ?? []).length > 0 &&
                                    <ul className={`issue-problems`}>
                                        {nfse.problems.map((problem, index) => (
                                            <li key={index}>
                                                {problem.message}
                                                {(problem.where === 'company' || problem.link) &&
                                                    <button onClick={() => (
                                                        problem.where === 'company'
                                                            ? editCompany([problem.field])
                                                            : navigate(`${problem.link}?return_to=/fiscal/settings`)
                                                    )}>
                                                        Corrigir agora
                                                    </button>
                                                }
                                            </li>
                                        ))}
                                    </ul>
                                }

                                {!nfse?.active && (nfse?.problems ?? []).length === 0 &&
                                    <p className={`fiscal-hint`}>
                                        Salve para concluir a ativação no emissor.
                                    </p>
                                }

                                <div className="row">
                                    <div className="col-6">
                                        <InputSelect
                                            menuInBody
                                            label={`Regime especial de ISS`}
                                            options={SPECIAL_TAX_REGIMES}
                                            value={`${form.special_tax_regime ?? '0'}`}
                                            change={value => updateForm({ special_tax_regime: value })}
                                        />
                                        <p className={`fiscal-hint`}>
                                            Só se a prefeitura concedeu um enquadramento especial à
                                            sua empresa — na dúvida, deixe "Nenhum".
                                        </p>
                                    </div>
                                </div>

                                <p className={`fiscal-hint`}>
                                    A numeração da nota de serviço é da prefeitura: a série e o
                                    número que você configura acima valem só para a NF-e.
                                </p>
                            </div>
                        }
                    </Box>
                }

                {/* As naturezas são do TENANT (valem para todas as empresas
                    emitentes), por isso ficam fora do bloco da empresa
                    selecionada — e aqui, não no menu: quem mexe nelas está
                    configurando a emissão. */}
                {!loading &&
                    <Box>
                        <span className={`fiscal-box-title`}>Naturezas de operação</span>
                        <p className={`fiscal-hint`}>
                            Definem o que a nota está fazendo (venda, remessa, devolução) e,
                            com isso, o CFOP e a tributação de cada item.
                        </p>

                        <div className={`fiscal-operations-summary`}>
                            {operations.length === 0 &&
                                <p className={`fiscal-hint`}>Nenhuma natureza cadastrada.</p>
                            }

                            {operations.slice(0, 6).map(operation => (
                                <button
                                    key={operation.id}
                                    className={`operation-chip`}
                                    onClick={() => navigate(`/fiscal/operations/${operation.id}?return_to=/fiscal/settings`)}
                                >
                                    <span>{operation.name}</span>
                                    {operation.is_default && <small>Padrão</small>}
                                </button>
                            ))}

                            {operations.length > 6 &&
                                <span className={`operation-more`}>
                                    +{operations.length - 6} {operations.length - 6 === 1 ? 'outra' : 'outras'}
                                </span>
                            }
                        </div>

                        <div className={`fiscal-operations-actions`}>
                            <Button
                                type={`secondary`}
                                size={`small`}
                                text={`Gerenciar naturezas`}
                                link={`/fiscal/operations`}
                            />
                            {canManage &&
                                <Button
                                    type={`secondary`}
                                    size={`small`}
                                    svg={`plus-blue`}
                                    text={`Nova natureza`}
                                    link={`/fiscal/operations/new`}
                                />
                            }
                        </div>
                    </Box>
                }
            </div>
        </PanelTemplate>
        </>
    );
};

export default FiscalSettings;
